import { useState, useCallback } from "react";
import { useNotifications } from "src/hooks/useNotifications";
import { adminService } from "src/services/services/admin/admin.api";
import {
  NotificationTypes,
  PubSubNotificationTypes,
} from "src/enums/Notifications";
import { FileType, LevelType, SubjectType } from "src/interfaces/KnowledgeBase";
import { documentService } from "src/services/services/documents/documents.api";
import { ResponseKind } from "src/services/services/MainService";
import { sendFailureNotification } from "src/utils/notifications";

interface FileUploadStatus {
  file: File;
  status: "idle" | "uploading" | "success" | "error";
}

export const useMultiFileUpload = () => {
  const [uploadStatuses, setUploadStatuses] = useState<FileUploadStatus[]>([]);
  const client = useNotifications()?.client;

  const uploadKnowledgebaseFiles = useCallback(
    async (
      files: File[],
      level: string,
      type: FileType,
      subject: SubjectType
    ): Promise<boolean[]> => {
      const newUploadStatuses = files.map((file) => ({
        file,
        status: "uploading" as const,
      }));
      setUploadStatuses(newUploadStatuses);

      const uploadPromises = files.map(async (file, index) => {
        try {
          const fileNames = await adminService.validateFiles([file.name]);
          if (fileNames.kind == ResponseKind.OK) {
            if (fileNames.data?.files[0].exists) {
              throw new Error("File already exists");
            }
          } else {
            throw new Error("Error validation files");
          }
          const res = await adminService.uploadKnowledgeDoc(
            file,
            level,
            type,
            subject.name
          );
          if (!res) {
            throw new Error("Error uploading file");
          }
          return new Promise<boolean>((resolve) => {
            const handleServerMessage = (data: any) => {
              if (data.message.data) {
                const message = JSON.parse(data.message.data.toString());
                console.log("response from pub sub", message);
                if (
                  message.event_type === PubSubNotificationTypes.fileUpload &&
                  message.file_name === file.name
                ) {
                  const messageLevel = String(message.level);
                  const status = String(message.status);
                  if (messageLevel === "Info" && status === "Completed") {
                    client?.off("group-message", handleServerMessage);
                    setUploadStatuses((prevStatuses) => {
                      const newStatuses = [...prevStatuses];
                      newStatuses[index] = {
                        ...newStatuses[index],
                        status: "success",
                      };
                      return newStatuses;
                    });
                    resolve(true);
                  } else if (messageLevel === "Error") {
                    sendFailureNotification(
                      NotificationTypes.fileUpload,
                      status
                    );
                    resolve(false);
                  }
                }
              }
            };

            client?.on("group-message", handleServerMessage);
          });
        } catch (error) {
          console.error("Error uploading file:", error);
          setUploadStatuses((prevStatuses) => {
            const newStatuses = [...prevStatuses];
            newStatuses[index] = { ...newStatuses[index], status: "error" };
            return newStatuses;
          });
          return false;
        }
      });

      client?.joinGroup("admin");
      return Promise.all(uploadPromises);
    },
    [client]
  );

  const resetUploadStatuses = useCallback(() => {
    setUploadStatuses([]);
  }, []);

  return {
    uploadStatuses,
    uploadKnowledgebaseFiles,
    resetUploadStatuses,
    client,
  };
};
